/**
 * Iter 185 — Header + Footer inspector panels for the site chrome.
 * ============================================================================
 *
 * Rendered inside the EditDrawer side panel when the user clicks the
 * canvas header or footer. Each inspector:
 *   • reads the current header/footer config from props (`value`)
 *   • calls `onChange(partial)` on every edit — debounced saves happen
 *     upstream in EditDrawer via useSiteChrome().save().
 *
 * Keeps the schema flat + JSON-serialisable. No refs to React state
 * outside of local UI state (open/closed accordion, etc.).
 */
import React, { useCallback } from 'react';
import { Plus, Trash2, Image as ImageIcon, Search as SearchIcon, ShoppingBag, ExternalLink, Instagram, Facebook, Twitter, Youtube, Linkedin, Music2, Anchor } from 'lucide-react';
import { Sortable, SortableRow, DragHandle } from './Sortable';

/* Reusable UI atoms — kept module-level so they NEVER have a per-render
   component identity (iter 183 pattern). */
const Section = ({ title, children }) => (
  <div className="border-t border-white/5 pt-4 mt-4">
    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] mb-3">{title}</p>
    {children}
  </div>
);

const TextInput = ({ label, value, onChange, placeholder = '', testid }) => (
  <label className="block mb-3">
    {label && <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1.5">{label}</p>}
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none"
      data-testid={testid}
    />
  </label>
);

const RadioRow = ({ label, options, value, onChange, testid }) => (
  <div className="mb-3">
    <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            value === o.value
              ? 'bg-[#A78BFA]/20 text-[#A78BFA] border border-[#A78BFA]/40'
              : 'bg-white/[0.04] text-white/70 border border-white/10 hover:bg-white/[0.08]'
          }`}
          data-testid={testid ? `${testid}-${o.value}` : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const nextId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 8)}`;


/* ─────────────────────────────────────────────────────────────
 *  HEADER INSPECTOR
 * ─────────────────────────────────────────────────────────── */
export function HeaderInspector({ value, onChange }) {
  const patch = useCallback((delta) => onChange({ ...value, ...delta }), [value, onChange]);
  const menu = value.menu_items || [];
  const buttons = value.buttons || [];

  const setMenu = (next) => patch({ menu_items: next });
  const setButtons = (next) => patch({ buttons: next });

  const addMenuItem = () => setMenu([...menu, { id: nextId('m'), label: 'New Page', href: '/', order: menu.length }]);
  const addButton = () => setButtons([...buttons, { id: nextId('b'), label: 'Shop Now', href: '/shop', variant: 'shop', order: buttons.length }]);

  const uploadLogo = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => patch({ logo_type: 'image', logo_image: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid="header-inspector">
      <Section title="Logo">
        <RadioRow
          label="Logo type"
          options={[
            { value: 'text', label: 'Text' },
            { value: 'image', label: 'Image' },
          ]}
          value={value.logo_type}
          onChange={(v) => patch({ logo_type: v })}
          testid="header-logo-type"
        />
        {value.logo_type === 'image' ? (
          <div>
            {value.logo_image && (
              <img src={value.logo_image} alt="logo" className="h-10 mb-2 bg-white/5 rounded p-1" />
            )}
            <label className="block px-3 py-2 rounded-lg bg-white/[0.04] border border-dashed border-white/20 text-center cursor-pointer text-white/70 text-xs hover:bg-white/[0.08]">
              <ImageIcon size={14} className="inline-block mr-1 -mt-0.5" />
              {value.logo_image ? 'Replace logo' : 'Upload logo image'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="header-logo-upload"
              />
            </label>
            {value.logo_image && (
              <button
                onClick={() => patch({ logo_image: '' })}
                className="text-[10px] text-white/40 hover:text-white/70 mt-2 uppercase tracking-wider"
                data-testid="header-logo-clear"
              >
                Clear logo image
              </button>
            )}
          </div>
        ) : (
          <TextInput label="Logo text" value={value.logo_text} onChange={(v) => patch({ logo_text: v })} placeholder="Your Brand" testid="header-logo-text" />
        )}
      </Section>

      <Section title="Layout">
        <RadioRow
          label="Logo position"
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ]}
          value={value.logo_position}
          onChange={(v) => patch({ logo_position: v })}
          testid="header-logo-position"
        />
        <RadioRow
          label="Menu style"
          options={[
            { value: 'plain', label: 'Plain' },
            { value: 'pill', label: 'Pill' },
            { value: 'underline', label: 'Underline' },
          ]}
          value={value.menu_style}
          onChange={(v) => patch({ menu_style: v })}
          testid="header-menu-style"
        />
      </Section>

      <Section title={`Menu (${menu.length})`}>
        <Sortable items={menu} onReorder={setMenu}>
          {menu.map((it) => (
            <SortableRow key={it.id} id={it.id}>
              {(handleProps) => (
                <div className="flex items-center gap-1.5 mb-2 p-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <DragHandle {...handleProps} data-testid={`header-menu-drag-${it.id}`} />
                  <div className="flex-1 space-y-1">
                    <input
                      value={it.label || ''}
                      onChange={(e) => setMenu(menu.map((x) => x.id === it.id ? { ...x, label: e.target.value } : x))}
                      placeholder="Label"
                      className="w-full px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs"
                      data-testid={`header-menu-label-${it.id}`}
                    />
                    <input
                      value={it.href || ''}
                      onChange={(e) => setMenu(menu.map((x) => x.id === it.id ? { ...x, href: e.target.value } : x))}
                      placeholder="/link"
                      className="w-full px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70 text-[10px]"
                      data-testid={`header-menu-href-${it.id}`}
                    />
                  </div>
                  <button onClick={() => setMenu(menu.filter((x) => x.id !== it.id).map((x, i2) => ({ ...x, order: i2 })))}
                    className="w-6 h-6 rounded flex items-center justify-center bg-red-500/10 hover:bg-red-500/20" data-testid={`header-menu-delete-${it.id}`}>
                    <Trash2 size={11} className="text-red-400" />
                  </button>
                </div>
              )}
            </SortableRow>
          ))}
        </Sortable>
        <button
          onClick={addMenuItem}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold hover:bg-[#A78BFA]/20"
          data-testid="header-menu-add"
        >
          <Plus size={12} /> Add menu item
        </button>
      </Section>

      <Section title={`Buttons (${buttons.length})`}>
        <Sortable items={buttons} onReorder={setButtons}>
          {buttons.map((b) => (
            <SortableRow key={b.id} id={b.id}>
              {(handleProps) => (
                <div className="flex items-center gap-1.5 mb-2 p-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <DragHandle {...handleProps} data-testid={`header-btn-drag-${b.id}`} />
                  <div className="flex-1 space-y-1">
                    <div className="flex gap-1">
                      {[
                        { v: 'shop', L: ShoppingBag, tip: 'Filled shop button' },
                        { v: 'search', L: SearchIcon, tip: 'Search input' },
                        { v: 'ghost', L: ExternalLink, tip: 'Text link' },
                      ].map(({ v, L, tip }) => (
                        <button
                          key={v}
                          onClick={() => setButtons(buttons.map((x) => x.id === b.id ? { ...x, variant: v } : x))}
                          className={`w-7 h-6 rounded flex items-center justify-center ${b.variant === v ? 'bg-[#A78BFA]/25 text-[#A78BFA]' : 'bg-white/5 text-white/50 hover:text-white/80'}`}
                          title={tip}
                          data-testid={`header-btn-variant-${b.id}-${v}`}
                        >
                          <L size={11} />
                        </button>
                      ))}
                    </div>
                    <input
                      value={b.label || ''}
                      onChange={(e) => setButtons(buttons.map((x) => x.id === b.id ? { ...x, label: (e.target.value || '').slice(0, 24) } : x))}
                      placeholder="Label (max 24)"
                      maxLength={24}
                      className="w-full px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs"
                      data-testid={`header-btn-label-${b.id}`}
                    />
                    <input
                      value={b.href || ''}
                      onChange={(e) => setButtons(buttons.map((x) => x.id === b.id ? { ...x, href: e.target.value } : x))}
                      placeholder={b.variant === 'search' ? '/search (optional)' : '/link or https://…'}
                      className="w-full px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70 text-[10px]"
                      data-testid={`header-btn-href-${b.id}`}
                    />
                  </div>
                  <button onClick={() => setButtons(buttons.filter((x) => x.id !== b.id).map((x, i2) => ({ ...x, order: i2 })))}
                    className="w-6 h-6 rounded flex items-center justify-center bg-red-500/10 hover:bg-red-500/20" data-testid={`header-btn-delete-${b.id}`}>
                    <Trash2 size={11} className="text-red-400" />
                  </button>
                </div>
              )}
            </SortableRow>
          ))}
        </Sortable>
        <button
          onClick={addButton}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold hover:bg-[#A78BFA]/20"
          data-testid="header-btn-add"
        >
          <Plus size={12} /> Add button
        </button>
      </Section>

      <Section title="Behaviour">
        <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          <input
            type="checkbox"
            checked={!!value.sticky}
            onChange={(e) => patch({ sticky: e.target.checked })}
            className="mt-0.5 w-4 h-4 accent-[#A78BFA]"
            data-testid="header-sticky"
          />
          <div className="flex-1">
            <p className="text-white text-sm font-bold flex items-center gap-1.5"><Anchor size={12} className="text-[#A78BFA]" /> Stick to top on scroll</p>
            <p className="text-[10px] text-white/50 mt-0.5">Header stays visible as visitors scroll down the page.</p>
          </div>
        </label>
      </Section>

      <Section title="Colors">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Background</p>
            <input type="color" value={value.bg_color || '#FFFFFF'} onChange={(e) => patch({ bg_color: e.target.value })} className="w-full h-9 rounded bg-white/5 cursor-pointer" data-testid="header-bg-color" />
          </label>
          <label className="block">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Text</p>
            <input type="color" value={value.fg_color || '#0E0E23'} onChange={(e) => patch({ fg_color: e.target.value })} className="w-full h-9 rounded bg-white/5 cursor-pointer" data-testid="header-fg-color" />
          </label>
        </div>
      </Section>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
 *  FOOTER INSPECTOR
 * ─────────────────────────────────────────────────────────── */
const SOCIAL_PLATFORMS = [
  { v: 'instagram', L: Instagram },
  { v: 'facebook', L: Facebook },
  { v: 'twitter', L: Twitter },
  { v: 'youtube', L: Youtube },
  { v: 'linkedin', L: Linkedin },
  { v: 'tiktok', L: Music2 },
];

export function FooterInspector({ value, onChange }) {
  const patch = useCallback((delta) => onChange({ ...value, ...delta }), [value, onChange]);
  const cols = value.columns || [];
  const social = value.social_links || [];
  const policies = value.policy_links || [];

  const setCols = (next) => patch({ columns: next });
  const setSocial = (next) => patch({ social_links: next });
  const setPolicies = (next) => patch({ policy_links: next });

  const uploadLogo = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => patch({ logo_type: 'image', logo_image: r.result });
    r.readAsDataURL(file);
  };

  return (
    <div data-testid="footer-inspector">
      <Section title="Logo & tagline">
        <RadioRow
          label="Logo type"
          options={[{ value: 'text', label: 'Text' }, { value: 'image', label: 'Image' }]}
          value={value.logo_type}
          onChange={(v) => patch({ logo_type: v })}
          testid="footer-logo-type"
        />
        {value.logo_type === 'image' ? (
          <div>
            {value.logo_image && <img src={value.logo_image} alt="logo" className="h-10 mb-2 bg-white/5 rounded p-1" />}
            <label className="block px-3 py-2 rounded-lg bg-white/[0.04] border border-dashed border-white/20 text-center cursor-pointer text-white/70 text-xs hover:bg-white/[0.08]">
              <ImageIcon size={14} className="inline-block mr-1 -mt-0.5" />
              {value.logo_image ? 'Replace logo' : 'Upload logo image'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="footer-logo-upload" />
            </label>
          </div>
        ) : (
          <TextInput label="Logo text" value={value.logo_text} onChange={(v) => patch({ logo_text: v })} placeholder="Your Brand" testid="footer-logo-text" />
        )}
        <TextInput label="Tagline" value={value.tagline} onChange={(v) => patch({ tagline: v })} placeholder="Handcrafted with love." testid="footer-tagline" />
      </Section>

      <Section title="Layout">
        <RadioRow
          label="Columns"
          options={[
            { value: 'stacked', label: 'Stacked' },
            { value: '2col', label: '2 Col' },
            { value: '3col', label: '3 Col' },
            { value: '4col', label: '4 Col' },
          ]}
          value={value.layout}
          onChange={(v) => patch({ layout: v })}
          testid="footer-layout"
        />
      </Section>

      <Section title={`Link columns (${cols.length})`}>
        <Sortable items={cols} onReorder={setCols}>
          {cols.map((c) => (
            <SortableRow key={c.id} id={c.id}>
              {(handleProps) => (
                <div className="mb-3 p-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <div className="flex items-center gap-1.5 mb-2">
                    <DragHandle {...handleProps} data-testid={`footer-col-drag-${c.id}`} />
                    <input value={c.title || ''} onChange={(e) => setCols(cols.map((x) => x.id === c.id ? { ...x, title: e.target.value } : x))} placeholder="Column title" className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs" data-testid={`footer-col-title-${c.id}`} />
                    <button onClick={() => setCols(cols.filter((x) => x.id !== c.id))} className="w-6 h-6 rounded bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center" data-testid={`footer-col-delete-${c.id}`}>
                      <Trash2 size={11} className="text-red-400" />
                    </button>
                  </div>
                  {(c.links || []).map((l, li) => (
                    <div key={li} className="flex gap-1 mb-1 pl-6">
                      <input value={l.label} onChange={(e) => setCols(cols.map((x) => x.id === c.id ? { ...x, links: x.links.map((y, y2) => y2 === li ? { ...y, label: e.target.value } : y) } : x))} placeholder="Label" className="flex-1 px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-white text-[11px]" />
                      <input value={l.href} onChange={(e) => setCols(cols.map((x) => x.id === c.id ? { ...x, links: x.links.map((y, y2) => y2 === li ? { ...y, href: e.target.value } : y) } : x))} placeholder="/link" className="flex-1 px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-white/70 text-[10px]" />
                      <button onClick={() => setCols(cols.map((x) => x.id === c.id ? { ...x, links: x.links.filter((_, y2) => y2 !== li) } : x))} className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center">
                        <Trash2 size={10} className="text-red-400" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => setCols(cols.map((x) => x.id === c.id ? { ...x, links: [...(x.links || []), { label: 'New link', href: '/' }] } : x))} className="ml-6 text-[10px] text-[#A78BFA] hover:text-white uppercase tracking-wider" data-testid={`footer-col-add-link-${c.id}`}>
                    + Add link
                  </button>
                </div>
              )}
            </SortableRow>
          ))}
        </Sortable>
        <button onClick={() => setCols([...cols, { id: nextId('c'), title: 'New Column', links: [] }])} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold hover:bg-[#A78BFA]/20" data-testid="footer-col-add">
          <Plus size={12} /> Add column
        </button>
      </Section>

      <Section title={`Social links (${social.length})`}>
        <Sortable items={social} onReorder={setSocial}>
          {social.map((s) => (
            <SortableRow key={s.id} id={s.id}>
              {(handleProps) => (
                <div className="flex items-center gap-1.5 mb-2">
                  <DragHandle {...handleProps} data-testid={`footer-social-drag-${s.id}`} />
                  <select value={s.platform} onChange={(e) => setSocial(social.map((x) => x.id === s.id ? { ...x, platform: e.target.value } : x))} className="px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs" data-testid={`footer-social-platform-${s.id}`}>
                    {SOCIAL_PLATFORMS.map((p) => <option key={p.v} value={p.v}>{p.v}</option>)}
                  </select>
                  <input value={s.url} onChange={(e) => setSocial(social.map((x) => x.id === s.id ? { ...x, url: e.target.value } : x))} placeholder="https://…" className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs" data-testid={`footer-social-url-${s.id}`} />
                  <button onClick={() => setSocial(social.filter((x) => x.id !== s.id))} className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center" data-testid={`footer-social-delete-${s.id}`}>
                    <Trash2 size={11} className="text-red-400" />
                  </button>
                </div>
              )}
            </SortableRow>
          ))}
        </Sortable>
        <button onClick={() => setSocial([...social, { id: nextId('s'), platform: 'instagram', url: '' }])} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold hover:bg-[#A78BFA]/20" data-testid="footer-social-add">
          <Plus size={12} /> Add social link
        </button>
      </Section>

      <Section title={`Policy links (${policies.length})`}>
        <Sortable items={policies} onReorder={setPolicies}>
          {policies.map((p) => (
            <SortableRow key={p.id} id={p.id}>
              {(handleProps) => (
                <div className="flex items-center gap-1.5 mb-2">
                  <DragHandle {...handleProps} data-testid={`footer-policy-drag-${p.id}`} />
                  <input value={p.label} onChange={(e) => setPolicies(policies.map((x) => x.id === p.id ? { ...x, label: e.target.value } : x))} placeholder="Label" className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs" data-testid={`footer-policy-label-${p.id}`} />
                  <input value={p.href} onChange={(e) => setPolicies(policies.map((x) => x.id === p.id ? { ...x, href: e.target.value } : x))} placeholder="/link" className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70 text-[10px]" data-testid={`footer-policy-href-${p.id}`} />
                  <button onClick={() => setPolicies(policies.filter((x) => x.id !== p.id))} className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center" data-testid={`footer-policy-delete-${p.id}`}>
                    <Trash2 size={11} className="text-red-400" />
                  </button>
                </div>
              )}
            </SortableRow>
          ))}
        </Sortable>
        <button onClick={() => setPolicies([...policies, { id: nextId('p'), label: 'New Policy', href: '/' }])} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold hover:bg-[#A78BFA]/20" data-testid="footer-policy-add">
          <Plus size={12} /> Add policy link
        </button>
      </Section>

      <Section title="Copyright">
        <TextInput
          label={'Text (use {year} + {brand} tokens)'}
          value={value.copyright_text}
          onChange={(v) => patch({ copyright_text: v })}
          placeholder="© {year} {brand}. All rights reserved."
          testid="footer-copyright"
        />
      </Section>

      <Section title="Colors">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Background</p>
            <input type="color" value={value.bg_color || '#0E0E23'} onChange={(e) => patch({ bg_color: e.target.value })} className="w-full h-9 rounded bg-white/5 cursor-pointer" data-testid="footer-bg-color" />
          </label>
          <label className="block">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Text</p>
            <input type="color" value={value.fg_color || '#FFFFFF'} onChange={(e) => patch({ fg_color: e.target.value })} className="w-full h-9 rounded bg-white/5 cursor-pointer" data-testid="footer-fg-color" />
          </label>
        </div>
      </Section>
    </div>
  );
}
